import { useState, useEffect } from 'react';
import api from '../../services/api';

const CustomerDeliveries = () => {
    const [deliveries, setDeliveries] = useState<any[]>([]);
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'history' | 'subscriptions'>('history');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [delRes, subRes] = await Promise.all([
                api.getPortalDeliveries(),
                api.getPortalSubscriptions()
            ]);
            
            if (delRes.success) setDeliveries(delRes.data);
            if (subRes.success) setSubscriptions(subRes.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="animate-pulse space-y-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-slate-200 rounded-3xl"></div>)}
    </div>;

    return (
        <div className="space-y-6 animate-in slide-in-from-bottom duration-500">
            {/* Tabs */}
            <div className="flex bg-slate-100 p-1.5 rounded-2xl w-fit">
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-6 py-2 rounded-xl text-sm font-black transition-all ${activeTab === 'history' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Delivery History
                </button>
                <button
                    onClick={() => setActiveTab('subscriptions')}
                    className={`px-6 py-2 rounded-xl text-sm font-black transition-all ${activeTab === 'subscriptions' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Subscriptions
                </button>
            </div>

            {activeTab === 'history' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {deliveries.length > 0 ? deliveries.map((delivery) => (
                        <div key={delivery.id} className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${delivery.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>
                                </div>
                                <div className="min-w-0">
                                    <p className="font-black text-slate-900 leading-tight">{delivery.product_name}</p>
                                    <p className="text-xs text-slate-500 font-bold uppercase tracking-tight mt-0.5">{new Date(delivery.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="font-black text-slate-900 leading-none">Qty: {delivery.quantity}</p>
                                <span className={`text-[9px] font-black uppercase tracking-widest mt-1 block ${delivery.status === 'delivered' ? 'text-emerald-500' : 'text-amber-500'}`}>{delivery.status}</span>
                            </div>
                        </div>
                    )) : (
                        <div className="md:col-span-2 text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200 text-slate-500 font-medium">
                            No delivery history yet
                        </div>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {subscriptions.length > 0 ? subscriptions.map((sub) => (
                        <div key={sub.id} className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm relative overflow-hidden group">
                            <div className="absolute top-0 right-0 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-bl-2xl text-[10px] font-black uppercase tracking-widest">
                                {sub.status}
                            </div>
                            <h4 className="text-xl font-black text-slate-900 mb-2">{sub.product_name}</h4>
                            <div className="grid grid-cols-2 gap-4 mt-6">
                                <div className="p-3 bg-slate-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Frequency</p>
                                    <p className="text-sm font-bold text-slate-700">{sub.frequency}</p>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Quantity</p>
                                    <p className="text-sm font-bold text-slate-700">{sub.quantity} Units</p>
                                </div>
                            </div>
                            <div className="mt-6 flex items-center justify-between pt-4 border-t border-slate-50">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></div>
                                    <span className="text-xs font-bold text-slate-500">Next: {new Date(sub.next_delivery_date).toLocaleDateString()}</span>
                                </div>
                                <button className="text-sm font-bold text-indigo-600 hover:underline">Manage</button>
                            </div>
                        </div>
                    )) : (
                        <div className="md:col-span-2 text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200 text-slate-500 font-medium">
                            No active subscriptions
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CustomerDeliveries;
